import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import SEO from "../components/SEO";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { ARRONDISSEMENTS, PHONE_DISPLAY, PHONE_TEL } from "../data/site";
import { VILLES } from "../data/zones";

const DEPTS = ["92", "93", "94", "77", "78", "91", "95"];

export default function ZonesIntervention() {
    return (
        <div data-testid="zones-page">
            <SEO
                title="Zones d'Intervention — Serrurier Paris & Île-de-France | 30 min, 24/7"
                description="Zones d'intervention Serrurier Paris : les 20 arrondissements de Paris (75001-75020) et l'Île-de-France — Boulogne, Vincennes, Saint-Denis, Versailles… Intervention en 30 min, 24h/24. ☎ 06 25 25 86 19"
                path="/zones-d-intervention"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Zones d'intervention</span>
                    </nav>
                    <SectionLabel dark>Paris & Île-de-France</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Zones d'intervention."]} />
                        <MaskedLines delay={0.45} lines={["30 min, partout."]} lineClassName="text-gold" />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Nos artisans serruriers sont positionnés dans tout Paris et l'Île-de-France : où que vous
                            soyez, un professionnel agréé assurance arrive chez vous en 30 minutes, 24h/24 et 7j/7.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* PARIS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Paris — 75</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        Les 20 arrondissements de Paris
                    </h2>
                </FadeUp>
                <div className="mt-10 grid grid-cols-2 gap-px bg-midnight/10 sm:grid-cols-4 lg:grid-cols-5" data-testid="zones-arrondissements">
                    {ARRONDISSEMENTS.map((a) => (
                        <Link
                            key={a.n}
                            to={`/serrurier-paris-${a.n}`}
                            data-testid={`zone-paris-${a.n}`}
                            className="group flex items-center justify-between bg-white px-5 py-4 text-midnight/80 transition-colors hover:bg-midnight hover:text-white"
                        >
                            <span className="font-semibold">Serrurier {a.label}</span>
                            <ArrowUpRight className="h-4 w-4 text-midnight/25 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold" />
                        </Link>
                    ))}
                </div>
            </section>


            {/* EDITORIAL */}
            <section className="bg-white py-14 md:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
                        <FadeUp>
                            <SectionLabel>Notre couverture</SectionLabel>
                            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                                Un serrurier disponible partout en Île-de-France
                            </h2>
                            <p className="mt-6 leading-relaxed text-midnight/70">
                                Nous disposons d'une équipe d'artisans serruriers répartis stratégiquement sur l'ensemble du territoire parisien et francilien. Cette organisation nous permet de garantir un délai d'intervention de 30 minutes maximum dans les 20 arrondissements de Paris, et dans toutes les grandes villes des départements 77, 78, 91, 92, 93, 94 et 95.
                            </p>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Que vous soyez à Paris 1er ou à Versailles, à Boulogne-Billancourt ou à Meaux, notre service de dépannage serrurerie 24h/24 et 7j/7 répond à toutes vos urgences : porte claquée, clé perdue, tentative de cambriolage, rideau métallique bloqué ou coffre-fort inaccessible.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    { stat: "20", label: "Arrondissements de Paris couverts" },
                                    { stat: "7", label: "Départements d'Île-de-France" },
                                    { stat: "30 min", label: "Délai d'intervention maximum" },
                                    { stat: "24h/7", label: "Service disponible en permanence" },
                                ].map((k) => (
                                    <div key={k.stat} className="border border-midnight/10 bg-cloud p-6">
                                        <span className="font-display text-4xl font-extrabold text-emergency">{k.stat}</span>
                                        <p className="mt-2 text-sm font-semibold text-midnight/70">{k.label}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 border-l-2 border-gold bg-cloud p-6">
                                <p className="text-sm font-bold text-midnight">Votre ville ne figure pas dans la liste ?</p>
                                <p className="mt-2 text-sm leading-relaxed text-midnight/70">
                                    Notre zone d'intervention dépasse la liste affichée ici. Appelez-nous : si un artisan peut intervenir chez vous dans un délai raisonnable, nous vous l'envoyons immédiatement.
                                </p>
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* IDF */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Île-de-France</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Serrurier dans les villes d'Île-de-France
                        </h2>
                    </FadeUp>
                    <div className="mt-12 space-y-10">
                        {DEPTS.map((d) => {
                            const villes = VILLES.filter((v) => v.dept === d);
                            if (!villes.length) return null;
                            return (
                                <FadeUp key={d}>
                                    <div data-testid={`zones-dept-${d}`}>
                                        <Link to={`/serrurier-${d}`} className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-midnight/40 transition-colors hover:text-emergency">
                                            <MapPin className="h-4 w-4 text-emergency" /> {d} — {villes[0].deptName}
                                        </Link>
                                        <div className="flex flex-wrap gap-2">
                                            {villes.map((v) => (
                                                <Link
                                                    key={v.slug}
                                                    to={`/serrurier-${v.slug}`}
                                                    data-testid={`zone-ville-${v.slug}`}
                                                    className="rounded-lg border border-midnight/10 bg-cloud px-4 py-2.5 text-sm font-semibold text-midnight/75 transition-all duration-200 hover:-translate-y-0.5 hover:border-emergency hover:text-emergency hover:shadow-lg"
                                                >
                                                    Serrurier {v.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>


            {/* FAQ ZONES */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Questions fréquentes</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        FAQ — Zones d'intervention
                    </h2>
                </FadeUp>
                <div className="mt-10 divide-y divide-midnight/10 border border-midnight/10 bg-white">
                    {[
                        {
                            q: "Intervenez-vous dans tous les arrondissements de Paris ?",
                            a: "Oui, nous couvrons les 20 arrondissements de Paris (75001 à 75020), sans exception, 24h/24 et 7j/7. Chaque arrondissement est desservi par au moins un artisan positionné à proximité pour garantir un délai d'intervention de 25 à 30 minutes.",
                        },
                        {
                            q: "Quels départements d'Île-de-France couvrez-vous ?",
                            a: "Nous intervenons dans les 7 départements de la grande couronne et petite couronne : Hauts-de-Seine (92), Seine-Saint-Denis (93), Val-de-Marne (94), Seine-et-Marne (77), Yvelines (78), Essonne (91) et Val-d'Oise (95). Si votre ville n'est pas listée, appelez-nous pour vérifier.",
                        },
                        {
                            q: "Le délai de 30 minutes est-il garanti la nuit et le week-end ?",
                            a: "Nous visons 30 minutes en toutes circonstances. En pratique, le délai peut légèrement varier selon l'heure et l'endroit — notamment en grande banlieue la nuit. Nos artisans vous donnent une estimation précise dès votre appel.",
                        },
                        {
                            q: "Les tarifs sont-ils les mêmes partout en Île-de-France ?",
                            a: "Les tarifs de base sont identiques à Paris et en petite couronne (92, 93, 94). Pour la grande couronne (77, 78, 91, 95), un supplément de déplacement peut s'appliquer selon la distance. Un devis gratuit est établi par téléphone avant toute intervention.",
                        },
                    ].map((item, i) => (
                        <FadeUp key={i} delay={i * 0.04}>
                            <details className="group">
                                <summary className="flex cursor-pointer items-center justify-between gap-4 px-6 py-5 font-display text-base font-bold tracking-tight marker:content-none hover:bg-cloud">
                                    {item.q}
                                    <span className="shrink-0 font-display text-xl text-emergency transition-transform duration-200 group-open:rotate-45">+</span>
                                </summary>
                                <div className="px-6 pb-5 text-sm leading-relaxed text-midnight/70">{item.a}</div>
                            </details>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Votre ville n'apparaît pas ? Appelez-nous.
                        </h2>
                        <p className="mt-3 text-white/60">Nous couvrons toute l'Île-de-France, 24h/24 et 7j/7.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="zones-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
